import { Form, Space, TableProps } from 'antd';
import { EyeIcon, HandoverIcon } from 'assets/svg';
import { ButtonAction } from 'components';
import TableComponent from 'components/Table/TableComponent';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import Content from 'layouts/Content';
import React, { forwardRef, memo, useImperativeHandle, useState } from 'react';
import formatNumber, {
  formatDate,
  formatNumber_v2,
  generateUUID,
} from 'utils/constants/functions';
import { HocChangePagination } from 'utils/HocChangePagination';
import ModalHandover from './ModalHandover';
import { useForm } from 'antd/es/form/Form';
import { useUser } from 'contexts/UserProvider';

interface IProps extends React.PropsWithChildren<TableProps> {
  data: any;
  refetch: (isExport?: boolean, headerRow?: any) => void;
  handleOnClickHandover: (record: any) => void;
}

const TableHandover = forwardRef((props: IProps, ref: any) => {
  const { data = {}, loading, refetch, handleOnClickHandover } = props;
  const { user } = useUser();
  const [form] = useForm();

  const columns: ColumnsTypeCustom = [
    {
      title: 'STT',
      width: 50,
      align: 'center',
      render: (_, __, index) =>
        data.pageInfo?.itemsPerPage * (data.pageInfo?.currentPage - 1) +
        index +
        1,
    },
    {
      title: 'Ngày thực hiện',
      width: 130,
      type: 'date',
      dataIndex: 'implementationDate',
      render: (value) => formatDate(value, 'DD/MM/YYYY'),
    },
    {
      title: 'Ngày kết thúc',
      width: 130,
      type: 'date',
      dataIndex: 'implementationEndDate',
      render: (value) => formatDate(value, 'DD/MM/YYYY'),
    },
    {
      title: 'Khách hàng',
      width: 150,
      dataIndex: 'sapCustomerId',
    },
    {
      title: 'Tên Khách hàng',
      width: 220,
      dataIndex: 'customerName',
    },
    {
      title: 'Mã KH cha',
      width: 150,
      dataIndex: 'mainCustomerId',
    },
    {
      title: 'Đội',
      width: 250,
      dataIndex: 'teamSalesName',
      render: (__, record: any) =>
        record?.teamSalesIdSap + ' - ' + record?.teamSalesName,
    },
    {
      title: 'Mã tuyến',
      width: 120,
      dataIndex: 'routeId',
    },
    {
      title: 'Tên tuyến',
      width: 200,
      dataIndex: 'routeName',
    },
    {
      title: 'TL Q10',
      width: 120,
      dataIndex: 'totalQ10',
      align: 'right',
      type: 'number',
      render: (value) => {
        // console.log('value', value);
        return formatNumber_v2(value);
      },
    },
    {
      title: 'Thành tiền KH thanh toán',
      width: 200,
      dataIndex: 'totalFinalPrice',
      align: 'right',
      type: 'number',
      render: (value) => formatNumber(value),
    },
    {
      title: 'TL Q10 sau bàn giao',
      width: 170,
      dataIndex: 'handOverQ10',
      align: 'right',
      type: 'number',
      render: (value) => formatNumber_v2(value),
    },
    {
      title: 'Thành tiền sau bàn giao',
      width: 180,
      dataIndex: 'handOverFinalPrice',
      align: 'right',
      type: 'number',
      render: (value) => formatNumber(value),
    },
    {
      title: 'Mã bảng kê',
      className: 'min-w-[200px] max-w-[350px]',
      dataIndex: 'orderIds',
      render: (value) => value?.split(',').join(', '),
    },
    {
      title: 'Mã bảng dẻ',
      className: 'min-w-[200px] max-w-[350px]',
      dataIndex: 'purchaseOrderIds',
      render: (value) => value?.split(',').join(', '),
    },
    {
      title: 'Mã BTHTT',
      className: 'min-w-[200px] max-w-[350px]',
      dataIndex: 'a1v2',
      render: (value) => value?.split(',').join(', '),
    },
    {
      title: 'Thủ kho xác nhận',
      width: 140,
      dataIndex: 'warehouseAdministerName',
    },
    {
      title: 'Thời gian thủ kho xác nhận',
      width: 200,
      dataIndex: 'warehouseDate',
      type: 'date',
      render: (value) => (value ? formatDate(value) : '-'),
    },
    {
      title: 'Thủ quỹ xác nhận',
      width: 140,
      dataIndex: 'treasurerName',
    },
    {
      title: 'Thời gian thủ quỷ xác nhận',
      width: 220,
      dataIndex: 'treasurerDate',
      type: 'date',
      render: (value) => (value ? formatDate(value) : '-'),
    },
    {
      title: 'Chi nhánh',
      width: 100,
      dataIndex: 'plantId',
    },
    {
      title: 'Trạng thái',
      width: 150,
      // dataIndex: '',
      render: (__, record) =>
        record?.treasurerId && record?.warehouseAdministerId
          ? 'Đã bàn giao'
          : record?.treasurerId
            ? 'Thủ quỹ xác nhận'
            : record?.warehouseAdministerId
              ? 'Thủ kho xác nhận'
              : 'Chưa bàn giao',
    },
    {
      title: 'Người tạo',
      width: 130,
      dataIndex: 'createName',
      render: (value) => 'Hệ thống',
    },
    // {
    //   title: 'Ngày tạo',
    //   width: 170,
    //   dataIndex: 'createDate',
    // type: 'date',
    //   render: (value) => (value ? formatDate(value) : '-'),
    // },
    {
      title: 'Người cập nhật',
      width: 130,
      dataIndex: 'modifyName',
    },
    {
      title: 'Ngày cập nhật',
      width: 170,
      type: 'date',
      dataIndex: 'modifyDate',
      render: (value) => (value ? formatDate(value) : '-'),
    },
    ...(user?.isStorekeeper || user?.isTreasurer
      ? ([
          {
            fixed: 'right',
            align: 'center',
            width: 70,
            render: (__, record, index) => (
              <Space size={'middle'} className="text-lg">
                {/* <ButtonAction tooltip="Xem chi tiết">
            <EyeIcon />
          </ButtonAction> */}
                <ButtonAction
                  tooltip="Xác nhận bàn giao"
                  onClick={() => handleOnClickHandover(record)}
                >
                  <HandoverIcon height={26} width={26} />
                </ButtonAction>
              </Space>
            ),
          },
        ] as ColumnsTypeCustom)
      : []),
  ];

  useImperativeHandle(ref, () => ({
    exportExcel,
  }));

  const exportExcel = () => {
    refetch(true, columns);
  };

  return (
    <Content>
      <TableComponent
        scroll={{ x: 'max-content' }}
        columns={columns}
        dataSource={data.items}
        // dataSource={data.items}
        rowKey={(record: any) => record?.id || generateUUID()}
        loading={loading}
        pagination={{
          current: data.pageInfo?.currentPage,
          pageSize: data.pageInfo?.itemsPerPage,
          total: data.pageInfo?.totalItems,
          onChange: HocChangePagination(),
        }}
      />
    </Content>
  );
});

export default TableHandover;
